<script lang="ts">
  import { Button, Title, Text, Image, Markdown } from '@lib/components';
  import { slide, responsiveStyle, parseResponsiveCSS, layoutStyles } from '@lib/directives';
  import { handleCtaNavigation } from '@lib/utils/navigation';
  import type { ElementBlock, BannerBlock as BannerBlockType } from '@lib/components/elements';
  import { HeroBlock, BannerBlock, FeatureBlock, CarouselBlock, MediaGridBlock, CardBlock, ScoresBlock, BlogArchiveBlock, ComparisonTableBlock } from '@lib/components/elements';
  import GridBlock from '@lib/components/elements/GridBlock/GridBlock.svelte';
  import LeaderboardBlock from '@lib/components/elements/LeaderboardBlock/LeaderboardBlock.svelte';

  export let block: ElementBlock;
  export let index: number = 0;
  export let baseDelay: number = 10;
  export let delayIncrement: number = 10;
  export let isFullscreen: boolean = false;
  export let onCtaClick: ((action?: string) => void) | undefined = undefined;

  $: bannerBlock = (block as any).type === 'banner' ? (block as any as BannerBlockType) : null;
  
  let blockPadding: string = '0';

  function getThemeColor(color: string) {
    const themeColors: Record<string, string> = {
      'primary': 'var(--theme-primary-color)',
      'secondary': 'var(--theme-secondary-color)',
      'tertiary': 'var(--theme-tertiary-color)',
      'accent': 'var(--theme-accent-color)',
      'success': 'var(--theme-success-color)',
      'warning': 'var(--theme-warning-color)',
      'error': 'var(--theme-error-color)',
      'info': 'var(--theme-info-color)'
    };
    return themeColors[color] || color;
  }

  function getBackgroundStyle() {
    if (!block.background) return '';
    
    const bg = block.background;
    
    if (bg.type === 'gradient' && bg.gradient) {
      const fromColor = getThemeColor(bg.gradient.from);
      const toColor = getThemeColor(bg.gradient.to);
      const direction = bg.gradient.direction || '135deg';
      return `background: linear-gradient(${direction}, ${fromColor}, ${toColor});`;
    }
    
    if (bg.type === 'color' && bg.value) {
      const color = getThemeColor(bg.value);
      return `background: ${color};`;
    }
    
    if (bg.type === 'image' && bg.image) {
      const imageUrl = bg.image.url || (bg.image.filename ? `/images/${bg.image.filename}` : '');
      if (!imageUrl) {
        throw new Error('Image background requires url or filename');
      }
      return `background-image: url('${imageUrl}'); background-size: cover; background-position: center;`;
    }
    
    return '';
  }
  
  // Smart padding logic:
  // 1. If explicit padding is set in YAML, use it
  // 2. If no explicit padding but has background color, use '1rem' (all sides)
  // 3. Otherwise, horizontal padding only '0 1rem'
  $: {
    const explicitPadding = block.padding;
    const hasBackground = block.background?.type === 'color';

    if (explicitPadding) {
      blockPadding = explicitPadding;
    } else if (hasBackground) {
      blockPadding = '1rem';
    } else {
      blockPadding = '0 1rem';
    }
  }

  // Parse CSS for responsive directive
  $: responsiveCSS = (block as any).css ? parseResponsiveCSS((block as any).css) : null;
  
  function getBlockStyles() {
    // For responsive CSS, we'll use the directive instead of inline styles
    if ((block as any).css && typeof (block as any).css === 'object') {
      return '';
    }
    
    // Legacy string CSS handling
    if (!(block as any).css || typeof (block as any).css !== 'string') return '';
    
    const cssRules = (block as any).css.trim();
    const rules = cssRules.split(';').filter((rule: string) => rule.trim());
    const importantRules = rules.map((rule: string) => {
      const trimmedRule = rule.trim();
      if (trimmedRule && !trimmedRule.includes('!important')) {
        return trimmedRule + ' !important';
      }
      return trimmedRule;
    });
    return importantRules.join('; ') + ';';
  }
</script>

<div
  id={block.id}
  class="screen-block"
  class:text={block.type === 'text'}
  class:image={block.type === 'image'}
  class:cta={block.type === 'cta'}
  class:carousel={block.type === 'carousel'}
  class:hero={block.type === 'hero'}
  class:banner={(block as any).type === 'banner'}
  class:feature={block.type === 'feature'}
  class:mediaGrid={block.type === 'mediaGrid'}
  class:card={block.type === 'card'}
  class:scores={block.type === 'scores'}
  class:grid={block.type === 'grid'}
  class:leaderboard={block.type === 'leaderboard'}
  class:comparisonTable={block.type === 'comparisonTable'}
  class:separator={block.type === 'separator'}
  class:center={block.alignment === 'center'}
  class:right={block.alignment === 'right'}
  class:light={(block as any).textColor === 'light'}
  class:primary={(block as any).textColor === 'primary'}
  class:secondary={(block as any).textColor === 'secondary'}
  class:dark={(block as any).textColor === 'dark'}
  class:full-width={(block as any).config?.fullWidth || (block as any).fullWidth || (bannerBlock && bannerBlock.layout?.width === 'full') || (block.type === 'separator' && (block as any).layout?.width === 'full') || (block.type === 'carousel' && (block as any).layout?.width === 'full') || (block.type === 'mediaGrid' && (block as any).layout?.width === 'full')}
  class:fullscreen={isFullscreen}
  class:sticky={bannerBlock && bannerBlock.sticky}
  style={getBlockStyles()}
  style:--block-padding={blockPadding || '0px'}
  style:scroll-margin-top="80px"
  use:responsiveStyle={responsiveCSS || {}}
>
  <div
    class="block-background"
    style={getBackgroundStyle()}
    style:--bg-color={block.background?.type === 'color' ? block.background.value : 'transparent'}
    style:--dark-bg-color={block.background?.type === 'color' && block.background.darkValue
      ? block.background.darkValue
      : undefined}
  ></div>

  <div class="block-content" use:layoutStyles={block}>
    {#if block.type === 'carousel'}
      <CarouselBlock {block} {isFullscreen} {onCtaClick} />
    {:else if block.type === 'hero'}
      <HeroBlock {block} {isFullscreen} {onCtaClick} />
    {:else if (block as any).type === 'banner'}
      <BannerBlock block={bannerBlock!} {isFullscreen} />
    {:else if block.type === 'feature'}
      <FeatureBlock {block} {isFullscreen} {onCtaClick} />
    {:else if block.type === 'mediaGrid'}
      <MediaGridBlock {block} />
    {:else if block.type === 'card'}
      <CardBlock {block} {isFullscreen} {onCtaClick} />
    {:else if block.type === 'scores'}
      <ScoresBlock {block} />
    {:else if block.type === 'grid'}
      <GridBlock {block} {onCtaClick} />
    {:else if block.type === 'leaderboard'}
      <LeaderboardBlock {block} {onCtaClick} />
    {:else if block.type === 'comparisonTable'}
      <ComparisonTableBlock {block} {isFullscreen} />
    {:else if block.type === 'blogArchive'}
      <BlogArchiveBlock {block} {isFullscreen} />
    {:else if block.type === 'text'}
      {#if block.markdown}
        <Markdown content={block.content} align={block.alignment || 'left'} />
      {:else}
        <Text align={block.alignment}>{block.content}</Text>
      {/if}
    {:else if block.type === 'image' && block.image}
      <div 
        class="image-wrapper"
        style:max-width={(block as any).layout?.width ? `${(block as any).layout.width}px` : undefined}
        style:margin={(block as any).layout?.width ? '0 auto' : undefined}
      >
        <Image
          src={block.image.url}
          alt={block.image.alt || ''}
          aspectRatio={block.image.aspectRatio}
          sources={block.image.sources}
          originalSrc={block.image.originalSrc}
          backgroundSize={(block as any).config?.backgroundSize || 'cover'}
        />
      </div>
    {:else if block.type === 'cta' && block.cta}
      <div class="cta-container">
        <Button
          label={block.cta.label}
          variant={block.cta.variant || 'primary'}
          bold={block.cta.bold || false}
          on:click={() => block.cta && handleCtaNavigation(block.cta, onCtaClick)}
          size="md"
        />
      </div>
    {:else if block.type === 'separator'}
      <div 
        class="separator-line"
        class:full-width={(block as any).layout?.width === 'full'}
        style:--separator-color={(block as any).color || 'var(--theme-text-color)'}
        style:--separator-height={(block as any).layout?.height || '2px'}
        style:--separator-width={(block as any).layout?.width && typeof (block as any).layout.width === 'number' ? `${(block as any).layout.width}px` : '100%'}
      ></div>
    {/if}
  </div>
</div>

<style lang="postcss">
  .screen-block {
    position: relative;
    isolation: isolate;
    color: var(--text-color);
    margin-bottom: 1.5rem;
    padding: var(--block-padding);
  }

  .block-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    background-color: var(--bg-color);
  }

  /* Dark mode support */
  :global([data-theme='dark']) .block-background {
    background-color: var(--dark-bg-color, var(--bg-color));
  }

  .screen-block.light {
    --text-color: #FFFFFF;
    color: #FFFFFF;
  }

  .screen-block.primary {
    --text-color: var(--theme-primary-color);
    color: var(--theme-primary-color);
  }

  .screen-block.secondary {
    --text-color: var(--theme-secondary-color);
    color: var(--theme-secondary-color);
  }

  .screen-block.dark {
    --text-color: var(--theme-text-on-primary);
    color: var(--theme-text-on-primary);
  }

  .screen-block.image {
    padding: 0 !important; /* Images should never have padding */
    overflow: hidden;
    position: relative;
  }







  .block-content {
    position: relative;
    z-index: 2;
    max-width: 800px;
    margin: 0 auto;
  }

  .screen-block.image .block-content {
    max-width: none;
    width: 100%;
  }
  
  /* In fullscreen mode, allow images to use more width */
  .screen-block.fullscreen.image .block-content {
    max-width: 800px;
    margin: 0 auto;
  }
  
  /* Hero blocks should not have content constraints */
  .screen-block.hero .block-content {
    max-width: none;
    width: 100%;
    height: 100%;
  }
  
  /* Banner blocks should not have content constraints */
  .screen-block.banner .block-content {
    max-width: none;
    width: 100%;
  }

  /* Separator blocks should not have content constraints */
  .screen-block.separator .block-content {
    max-width: none;
    width: 100%;
  }
  
  /* Banner block specific styles */
  .screen-block.banner {
    padding: 0 !important; /* Banners handle their own padding internally */
    margin-bottom: 0;
  }
  
  /* Sticky banner positioning */
  .screen-block.banner.sticky {
    position: sticky;
    top: 0;
    z-index: 100;
  }
  
  /* Full-width blocks remove content constraints */
  .screen-block.full-width .block-content {
    max-width: none;
  }
  
  .screen-block.hero {
    padding: 0 !important; /* Heroes handle their own padding */
    margin-bottom: 2rem;
    overflow: hidden;
  }

  .screen-block.carousel {
    padding: 0 !important; /* Carousels are transparent wrappers */
    margin-bottom: 2rem;
  }

  .screen-block.carousel .block-content {
    max-width: none;
    width: 100%;
  }

  .screen-block.feature {
    padding: 0 !important; /* Features handle their own padding */
    margin-bottom: 2rem;
  }

  .screen-block.mediaGrid {
    padding: 0 1rem !important; /* MediaGrid needs some horizontal padding */
    margin-bottom: 2rem;
  }

  .screen-block.mediaGrid .block-content {
    max-width: none;
    width: 100%;
  }

  .screen-block.separator {
    padding: 2rem 1rem !important;
  }

  /* Full-width separators extend to edges by removing base screen-block padding */
  .screen-block.fullscreen.separator {
    padding: 2rem 0 !important;
  }

  .separator-line {
    width: var(--separator-width, 100%);
    height: var(--separator-height, 2px);
    background-color: var(--separator-color, var(--theme-text-color));
    opacity: 0.5;
    margin: 0 auto; /* Center when width is constrained */
  }

  .separator-line.full-width {
    width: 100%;
    max-width: none;
  }

  .screen-block.hero .block-background {
    display: none; /* Heroes handle their own backgrounds */
  }

  .image-wrapper {
    width: 100%;
    display: block;
    line-height: 0;
  }

  .cta-container {
    margin-top: 1.5rem;
    display: flex;
    width: auto;
    min-width: 200px;
  }

  .screen-block.cta .cta-container {
    margin-top: 0;
  }

  .screen-block.center {
    text-align: center;
  }

  .screen-block.right {
    text-align: right;
  }

  @container (max-width: 280px) {
    .cta-container {
      margin-top: 1rem;
    }
  }

  /* Full-width blocks fill their container */
  .screen-block.full-width {
    width: 100%;
  }

  .screen-block.full-width.image {
    margin-bottom: 1.5rem;
  }

  /* In fullscreen mode, full-width blocks should extend to container edges */
  :global(.screen-component.fullscreen) .screen-block.full-width {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
    max-width: 100vw;
  }

  /* Ensure images in fullscreen mode respect the max-width */
  :global(.screen-component.fullscreen) .screen-block.image {
    max-width: 800px;
    margin: 0 auto 1.5rem;
  }

  /* Full-width images in fullscreen can go wider */
  :global(.screen-component.fullscreen) .screen-block.full-width.image {
    max-width: 100vw;
    width: 100vw;
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
  }
</style> 